
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";

const MidCenturyModernADUDesignLosAltosHills = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Mid-Century Modern ADU Design & Construction in Los Altos Hills",
    "description": "Custom Mid-Century Modern ADUs in Los Altos Hills. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/mid-century-modern-adu-design-los-altos-hills"
  };

  return (
    <div className="min-h-screen bg-background">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Mid-Century Modern ADU Design & Construction in Los Altos Hills
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Sophisticated Mid-Century Modern accessory dwelling units designed for Los Altos Hills' exclusive hillside estates and natural beauty.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Mid-Century Modern ADUs in Los Altos Hills?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Hillside Integration</h3>
              <p className="text-gray-300">Mid-Century Modern design that seamlessly integrates with Los Altos Hills' dramatic topography and views.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Luxury Heritage</h3>
              <p className="text-gray-300">Classic architectural style that complements the area's prestigious estate homes and natural setting.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Estate Value</h3>
              <p className="text-gray-300">Premium investment in one of Silicon Valley's most exclusive and sought-after communities.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Mid-Century Modern ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Floor-to-ceiling windows maximizing hillside views</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Flat or low-pitched rooflines</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Post-and-beam construction with open floor plans</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Natural materials: wood, stone, and steel</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Seamless indoor-outdoor living spaces</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Los Altos Hills-Specific Mid-Century Modern ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              In Los Altos Hills, Mid-Century Modern ADUs must comply with specific local requirements:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Hillside development and grading regulations</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Large estate lot setback requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Tree preservation and native landscape protection</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Fire safety and wildland interface requirements</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our team navigates Los Altos Hills' unique requirements to deliver stunning Mid-Century Modern ADUs.
            </p>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Mid-Century Modern ADU Cost in Los Altos Hills
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Mid-Century Modern ADU in Los Altos Hills?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Let's discuss your Mid-Century Modern ADU project and create something exceptional for your Los Altos Hills estate.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in Los Altos Hills:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/los-altos-hills-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in Los Altos Hills
              </Link>
              <Link to="/adu-cost-los-altos-hills" className="text-[#FF7A00] hover:underline">
                ADU Cost in Los Altos Hills
              </Link>
              <Link to="/adu-regulations-los-altos-hills" className="text-[#FF7A00] hover:underline">
                ADU Regulations in Los Altos Hills
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Mid-Century Modern ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/mid-century-modern-adu-design-los-altos" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU Los Altos
              </Link>
              <Link to="/mid-century-modern-adu-design-palo-alto" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU Palo Alto
              </Link>
              <Link to="/mid-century-modern-adu-design-mountain-view" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU Mountain View
              </Link>
              <Link to="/mid-century-modern-adu-design-san-jose" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU San Jose
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default MidCenturyModernADUDesignLosAltosHills;
